import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useSalon } from "../context/SalonContext";
import { WEEKLY_SALES } from "../data/seed";
import api from "../lib/api";

// ─── Hub components ─────────────────────────────────────────────────────
const FinancialOverviewChart = ({ data, brandColor = "#f59e0b" }) => {
    // SVG line chart (bruto = âmbar, líquido = verde). Sem dependência.
    if (!data || data.length === 0) {
        return <div className="text-xs text-slate-500 text-center py-10">Sem dados históricos.</div>;
    }
    const W = 720, H = 200, PAD_L = 40, PAD_R = 12, PAD_T = 16, PAD_B = 26;
    const IW = W - PAD_L - PAD_R, IH = H - PAD_T - PAD_B;
    const maxV = Math.max(1, ...data.map((d) => Math.max(d.gross || 0, d.net || 0)));
    const xStep = data.length > 1 ? IW / (data.length - 1) : IW;
    const y = (v) => PAD_T + IH - (v / maxV) * IH;
    const x = (i) => PAD_L + i * xStep;
    const pathGross = data.map((d, i) => `${i === 0 ? "M" : "L"}${x(i)},${y(d.gross || 0)}`).join(" ");
    const pathNet   = data.map((d, i) => `${i === 0 ? "M" : "L"}${x(i)},${y(d.net || 0)}`).join(" ");
    const area = `${pathGross} L${x(data.length - 1)},${PAD_T + IH} L${x(0)},${PAD_T + IH} Z`;
    const fmt = (v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v.toFixed(0));
    return (
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-56" data-testid="financial-overview-chart">
            <defs>
                <linearGradient id="grossArea" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor={brandColor} stopOpacity="0.35" />
                    <stop offset="100%" stopColor={brandColor} stopOpacity="0" />
                </linearGradient>
            </defs>
            {/* Y axis grid + labels */}
            {[0, 0.25, 0.5, 0.75, 1].map((f, idx) => (
                <g key={idx}>
                    <line x1={PAD_L} x2={W - PAD_R} y1={y(maxV * f)} y2={y(maxV * f)} stroke="rgba(148,163,184,0.08)" />
                    <text x={PAD_L - 6} y={y(maxV * f) + 3} fontSize="9" fill="#64748b" textAnchor="end" fontFamily="monospace">
                        {fmt(maxV * f)}
                    </text>
                </g>
            ))}
            {/* Area bruto */}
            <path d={area} fill="url(#grossArea)" />
            {/* Line bruto */}
            <path d={pathGross} fill="none" stroke={brandColor} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
            {/* Line líquido */}
            <path d={pathNet} fill="none" stroke="#34d399" strokeWidth="1.8" strokeDasharray="3 3" strokeLinecap="round" strokeLinejoin="round" />
            {/* Dots */}
            {data.map((d, i) => (
                <g key={i}>
                    <circle cx={x(i)} cy={y(d.gross || 0)} r="3" fill={brandColor} stroke="#0b1220" strokeWidth="1.5">
                        <title>{d.label} · Bruto R$ {(d.gross || 0).toFixed(0)} · Líquido R$ {(d.net || 0).toFixed(0)} · {d.atendimentos || 0} atend. · Ticket R$ {(d.avg_ticket || 0).toFixed(0)}</title>
                    </circle>
                    <text x={x(i)} y={H - 8} fontSize="10" fill="#94a3b8" textAnchor="middle">{d.label}</text>
                </g>
            ))}
            {/* Legend */}
            <g transform={`translate(${W - PAD_R - 160}, ${PAD_T - 6})`}>
                <rect x="0" y="0" width="12" height="2" fill={brandColor} />
                <text x="18" y="4" fontSize="10" fill="#cbd5e1">Bruto</text>
                <line x1="60" y1="1" x2="72" y2="1" stroke="#34d399" strokeWidth="1.8" strokeDasharray="3 3" />
                <text x="78" y="4" fontSize="10" fill="#cbd5e1">Líquido</text>
            </g>
        </svg>
    );
};

const KpiChip = ({ icon, label, value, tone = "slate" }) => {
    const toneCls = tone === "emerald" ? "text-emerald-300" : tone === "amber" ? "text-amber-300" : "text-white";
    return (
        <div className="flex items-center gap-2 rounded-lg border border-white/8 bg-slate-900/40 px-3 py-2">
            <i className={`fa-solid ${icon} text-slate-500 text-xs`}></i>
            <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</span>
            <span className={`text-sm font-mono font-bold ${toneCls}`}>{value}</span>
        </div>
    );
};

const NavCard = ({ testId, icon, title, description, badge, onClick }) => (
    <button
        onClick={onClick}
        data-testid={testId}
        className="rounded-2xl border border-white/8 bg-slate-900/40 hover:border-amber-500/40 hover:bg-amber-500/5 backdrop-blur-xl p-5 text-left transition-all group flex flex-col gap-3 w-full"
    >
        <div className="flex items-center justify-between">
            <div className="h-12 w-12 rounded-xl bg-amber-500/10 flex items-center justify-center group-hover:bg-amber-500/20">
                <i className={`fa-solid ${icon} text-amber-400 text-xl`}></i>
            </div>
            <i className="fa-solid fa-arrow-right text-slate-600 group-hover:text-amber-300 group-hover:translate-x-1 transition-all"></i>
        </div>
        <div>
            <div className="font-semibold text-white text-sm">{title}</div>
            <div className="text-xs text-slate-400 mt-0.5">{description}</div>
        </div>
        {badge && (
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider inline-block w-fit ${
                badge === "red"
                    ? "bg-red-500/20 text-red-300 border border-red-500/30"
                    : "bg-amber-500/20 text-amber-300 border border-amber-500/30"
            }`}>
                {badge === "red" ? "Atenção" : "Pendente"}
            </span>
        )}
    </button>
);

const BackButton = ({ onBack }) => (
    <button
        data-testid="financial-back-btn"
        onClick={onBack}
        className="h-10 w-10 rounded-xl border border-white/10 hover:border-amber-500/40 text-slate-400 hover:text-amber-300 flex items-center justify-center transition-all mb-4"
    >
        <i className="fa-solid fa-arrow-left"></i>
    </button>
);

/**
 * Module E — Painel Financeiro Administrativo
 * Locked behind a touchscreen numeric PIN keypad. PIN: 1234
 */
const FinancialPanel = () => {
    const { brand, professionals, comandas, adminUnlocked, setAdminUnlocked } = useSalon();
    const [pin, setPin] = useState("");

    if (!adminUnlocked) {
        return <PinLockScreen pin={pin} setPin={setPin} onUnlock={() => setAdminUnlocked(true)} brand={brand} expectedPin={brand.financialPin || "1234"} />;
    }

    return <AdminDashboard brand={brand} professionals={professionals} comandas={comandas} onLock={() => setAdminUnlocked(false)} />;
};

const PinLockScreen = ({ pin, setPin, onUnlock, brand, expectedPin }) => {
    const [shaking, setShaking] = useState(false);
    const handleDigit = (d) => {
        if (pin.length >= 4) return;
        const next = pin + d;
        setPin(next);
        if (next.length === 4) {
            setTimeout(() => {
                if (next === expectedPin) {
                    toast.success("Acesso liberado", { description: "Painel administrativo destravado." });
                    onUnlock();
                    setPin("");
                } else {
                    toast.error("PIN incorreto", { description: "Verifique o PIN definido no cadastro do salão." });
                    setShaking(true);
                    setTimeout(() => {
                        setShaking(false);
                        setPin("");
                    }, 600);
                }
            }, 250);
        }
    };
    const handleBack = () => setPin(pin.slice(0, -1));
    const handleClear = () => setPin("");

    return (
        <div
            data-testid="pin-lock-screen"
            className="grid place-items-center min-h-[calc(100vh-130px)]"
        >
            <div className={`relative rounded-3xl border border-white/10 bg-slate-900/80 backdrop-blur-xl p-8 md:p-10 w-full max-w-md shadow-2xl ${shaking ? "animate-[shake_0.4s]" : ""}`}>
                <div className="flex flex-col items-center text-center">
                    <div
                        className="h-16 w-16 rounded-2xl flex items-center justify-center mb-5"
                        style={{ background: `${brand.primaryColor}22`, color: brand.primaryColor }}
                    >
                        <i className="fa-solid fa-lock text-2xl"></i>
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500">
                        Painel Bloqueado
                    </div>
                    <h2 className="salon-heading text-2xl text-white mt-1">Insira o PIN Admin</h2>
                    <p className="text-xs text-slate-400 mt-2">
                        Acesso exclusivo do dono · 4 dígitos definidos no cadastro.
                    </p>
                </div>

                <div className="mt-7 flex justify-center gap-3">
                    {[0, 1, 2, 3].map((i) => (
                        <div
                            key={i}
                            className="h-14 w-12 rounded-xl border border-white/10 bg-slate-950/60 flex items-center justify-center text-2xl font-bold text-white"
                            style={pin[i] ? { borderColor: brand.primaryColor, color: brand.primaryColor } : {}}
                        >
                            {pin[i] ? "•" : ""}
                        </div>
                    ))}
                </div>

                <div className="mt-7 grid grid-cols-3 gap-3">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                        <button
                            key={n}
                            data-testid={`pin-key-${n}`}
                            onClick={() => handleDigit(String(n))}
                            className="min-h-[64px] rounded-2xl bg-slate-950/60 hover:bg-slate-950 active:scale-95 border border-white/5 text-white text-xl font-semibold transition-all"
                        >
                            {n}
                        </button>
                    ))}
                    <button
                        data-testid="pin-key-clear"
                        onClick={handleClear}
                        className="min-h-[64px] rounded-2xl bg-slate-950/60 hover:bg-slate-950 border border-white/5 text-slate-400 text-xs font-semibold uppercase tracking-wider transition-all"
                    >
                        Limpar
                    </button>
                    <button
                        data-testid="pin-key-0"
                        onClick={() => handleDigit("0")}
                        className="min-h-[64px] rounded-2xl bg-slate-950/60 hover:bg-slate-950 active:scale-95 border border-white/5 text-white text-xl font-semibold transition-all"
                    >
                        0
                    </button>
                    <button
                        data-testid="pin-key-back"
                        onClick={handleBack}
                        className="min-h-[64px] rounded-2xl bg-slate-950/60 hover:bg-slate-950 border border-white/5 text-slate-400 transition-all"
                    >
                        <i className="fa-solid fa-delete-left"></i>
                    </button>
                </div>
            </div>
            <style>{`@keyframes shake { 0%,100%{transform:translateX(0)} 20%,60%{transform:translateX(-8px)} 40%,80%{transform:translateX(8px)} }`}</style>
        </div>
    );
};

const AdminDashboard = ({ brand, professionals, comandas, onLock }) => {
    const { payables, markPayablePaid, upsertPayable, deletePayable, payCommission } = useSalon();
    const [view, setView] = useState("hub"); // hub | faturamento | comissoes | contas | pulse
    const [period, setPeriod] = useState(30);
    const [history, setHistory] = useState([]);
    const [historyRange, setHistoryRange] = useState(6);

    // Fetch histórico mensal do backend
    useEffect(() => {
        let alive = true;
        api.get(`/tenant/financial/history?months=${historyRange}`).then((d) => {
            if (alive && Array.isArray(d)) setHistory(d);
        }).catch(() => { /* silent — mantém fallback vazio */ });
        return () => { alive = false; };
    }, [historyRange]);

    // Real consolidated metrics aggregated from professionals.transactions
    const consolidated = useMemo(() => {
        const since = Date.now() - period * 86400000;
        const allTxns = [];
        professionals.forEach((p) => {
            (p.transactions || []).forEach((t) => {
                if (t.date >= since) allTxns.push({ ...t, proId: p.id, proName: p.name, proAvatar: p.avatar });
            });
        });
        const revenue = allTxns.reduce((s, t) => s + (t.total || 0), 0);
        const commissions = allTxns.reduce((s, t) => s + (t.share || 0), 0);
        const count = allTxns.length;
        const ticketAvg = count > 0 ? revenue / count : 0;

        const proAgg = {};
        allTxns.forEach((t) => {
            if (!proAgg[t.proId]) proAgg[t.proId] = { id: t.proId, name: t.proName, avatar: t.proAvatar, revenue: 0, count: 0, commissions: 0 };
            proAgg[t.proId].revenue += t.total || 0;
            proAgg[t.proId].count += 1;
            proAgg[t.proId].commissions += t.share || 0;
        });
        const topPros = Object.values(proAgg).sort((a, b) => b.revenue - a.revenue);

        const svcAgg = {};
        allTxns.forEach((t) => {
            const k = t.service || "Outros";
            if (!svcAgg[k]) svcAgg[k] = { name: k, revenue: 0, count: 0 };
            svcAgg[k].revenue += t.total || 0;
            svcAgg[k].count += 1;
        });
        const topServices = Object.values(svcAgg).sort((a, b) => b.revenue - a.revenue);

        const buckets = Math.min(period, 30);
        const bucketSize = (period * 86400000) / buckets;
        const series = Array.from({ length: buckets }, (_, i) => {
            const start = since + i * bucketSize;
            const end = start + bucketSize;
            const items = allTxns.filter((t) => t.date >= start && t.date < end);
            const total = items.reduce((s, t) => s + (t.total || 0), 0);
            const label = new Date(start).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
            return { label, total, count: items.length };
        });

        return { revenue, commissions, count, ticketAvg, topPros, topServices, series };
    }, [professionals, period]);

    const metrics = useMemo(() => {
        const grossToday = 4280 + comandas.reduce((s, c) => s + c.items.reduce((x, i) => x + i.price * i.qty, 0), 0);
        const completed = 38;
        const avgTicket = grossToday / Math.max(1, completed);
        const pendingCommissions = professionals.reduce((s, p) => s + (p.commissionAccrued || 0), 0);

        const startOfDay = new Date(); startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = startOfDay.getTime() + 86400000;
        const todayPayablesDue = payables
            .filter((p) => p.status === "open" && p.dueDate < endOfDay)
            .reduce((s, p) => s + p.amount, 0);
        const todayCommissionAccrual = pendingCommissions * 0.05; // approx daily share — visual only

        const netToday = grossToday - todayPayablesDue - todayCommissionAccrual;
        const totalOpenPayables = payables.filter((p) => p.status === "open").reduce((s, p) => s + p.amount, 0);
        const overduePayables = payables.filter((p) => p.status === "open" && p.dueDate < Date.now()).length;
        return { grossToday, netToday, completed, avgTicket, pendingCommissions, todayPayablesDue, totalOpenPayables, overduePayables };
    }, [comandas, professionals, payables]);

    const maxSales = Math.max(...WEEKLY_SALES.map((w) => w.services + w.products));

    // ─── Hub principal — tela default após PIN ─────────────────────────
    if (view === "hub") {
        return (
            <div data-testid="financial-hub" className="space-y-6">
                <header className="flex items-center justify-between gap-4 flex-wrap">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500">Módulo E · Acesso restrito</div>
                        <h2 className="salon-heading text-2xl text-white mt-1">Painel Financeiro</h2>
                    </div>
                    <button
                        data-testid="lock-financial-btn"
                        onClick={onLock}
                        className="min-h-[44px] px-4 rounded-xl border border-white/10 hover:border-red-500/40 text-slate-400 hover:text-red-300 text-xs uppercase tracking-wider transition-all flex items-center gap-2"
                    >
                        <i className="fa-solid fa-lock"></i>Bloquear
                    </button>
                </header>

                {/* Gráfico principal */}
                <div className="rounded-2xl border border-white/5 bg-slate-900/40 backdrop-blur-xl p-5">
                    <div className="flex items-center justify-between mb-3">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300">Visão geral</div>
                            <h3 className="salon-heading text-lg text-white mt-0.5">Faturamento mensal</h3>
                        </div>
                        <div className="inline-flex rounded-lg border border-white/10 p-0.5 bg-slate-950/40">
                            {[["6m", 6], ["12m", 12], ["24m", 24]].map(([lbl, v]) => (
                                <button key={v} onClick={() => setHistoryRange(v)}
                                    data-testid={`financial-range-${v}`}
                                    className={`px-3 py-1 text-[10px] uppercase tracking-widest rounded transition-all ${historyRange === v ? "bg-amber-500/20 text-amber-200" : "text-slate-500 hover:text-white"}`}>
                                    {lbl}
                                </button>
                            ))}
                        </div>
                    </div>
                    <FinancialOverviewChart data={history} brandColor={brand.primaryColor || "#f59e0b"} />
                </div>

                {/* KPI chips do dia */}
                <div className="flex gap-2 flex-wrap" data-testid="financial-kpi-chips">
                    <KpiChip icon="fa-money-bill" label="Bruto hoje" value={`R$ ${metrics.grossToday.toFixed(0)}`} tone="amber" />
                    <KpiChip icon="fa-circle-check" label="Líquido hoje" value={`R$ ${metrics.netToday.toFixed(0)}`} tone="emerald" />
                    <KpiChip icon="fa-scissors" label="Atendimentos" value={metrics.completed} />
                    <KpiChip icon="fa-ticket" label="Ticket médio" value={`R$ ${metrics.avgTicket.toFixed(0)}`} />
                </div>

                {/* 4 cards de navegação */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <NavCard
                        testId="financial-card-faturamento" icon="fa-cash-register"
                        title="Faturamento & Período"
                        description="Análise consolidada e tendências"
                        onClick={() => setView("faturamento")}
                    />
                    <NavCard
                        testId="financial-card-comissoes" icon="fa-hand-holding-dollar"
                        title="Comissões & Equipe"
                        description={metrics.pendingCommissions > 0
                            ? `R$ ${metrics.pendingCommissions.toFixed(0)} pendentes`
                            : "Todas em dia"}
                        badge={metrics.pendingCommissions > 0 ? "amber" : null}
                        onClick={() => setView("comissoes")}
                    />
                    <NavCard
                        testId="financial-card-contas" icon="fa-file-invoice-dollar"
                        title="Contas a Pagar"
                        description={metrics.overduePayables > 0 ? `${metrics.overduePayables} vencidas` : "Em dia"}
                        badge={metrics.overduePayables > 0 ? "red" : null}
                        onClick={() => setView("contas")}
                    />
                    <NavCard
                        testId="financial-card-pulse" icon="fa-chart-line"
                        title="Pulse do Salão"
                        description="Operação em tempo real"
                        onClick={() => setView("pulse")}
                    />
                </div>
            </div>
        );
    }

    // ═══ VIEW 1 — FATURAMENTO & PERÍODO ══════════════════════════════════════
    if (view === "faturamento") {
        return (
            <SubViewShell view={view} title="Faturamento & Período" onBack={() => setView("hub")} onLock={onLock}>
                <div data-testid="financial-consolidated" className="rounded-2xl border border-amber-500/20 bg-slate-900/40 backdrop-blur-xl p-6 space-y-5">
                    <header className="flex items-center justify-between gap-4 flex-wrap">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.25em] text-amber-300/70 font-bold">Painel Consolidado</div>
                            <h3 className="salon-heading text-lg text-white mt-1">Faturamento do salão · período</h3>
                        </div>
                        <div className="flex items-center gap-1 p-1 rounded-xl border border-white/8 bg-black/40">
                            {[{id:7,label:"7 dias"},{id:15,label:"15 dias"},{id:30,label:"30 dias"},{id:60,label:"60 dias"},{id:90,label:"90 dias"}].map((p) => {
                                const active = period === p.id;
                                return (
                                    <button
                                        key={p.id}
                                        data-testid={`fin-period-${p.id}`}
                                        onClick={() => setPeriod(p.id)}
                                        className={`min-h-[36px] px-3 rounded-lg text-[10px] uppercase tracking-wider font-bold transition-all ${active ? "bg-amber-500 text-slate-950" : "text-slate-400 hover:text-amber-200"}`}
                                    >
                                        {p.label}
                                    </button>
                                );
                            })}
                        </div>
                    </header>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <ConsolidatedKpi icon="fa-cash-register" label={`Faturamento ${period}d`} value={`R$ ${consolidated.revenue.toFixed(2)}`} accent="emerald" testId="fin-cons-revenue" />
                        <ConsolidatedKpi icon="fa-scissors" label="Atendimentos" value={consolidated.count} accent="amber" testId="fin-cons-count" />
                        <ConsolidatedKpi icon="fa-receipt" label="Ticket Médio" value={`R$ ${consolidated.ticketAvg.toFixed(2)}`} accent="amber" testId="fin-cons-ticket" />
                        <ConsolidatedKpi icon="fa-hand-holding-dollar" label="Comissões" value={`R$ ${consolidated.commissions.toFixed(2)}`} accent="slate" sub={`${consolidated.revenue > 0 ? ((consolidated.commissions / consolidated.revenue) * 100).toFixed(1) : 0}% do bruto`} testId="fin-cons-commissions" />
                    </div>

                    <div className="rounded-xl border border-white/5 bg-black/40 p-4">
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Tendência de receita · {consolidated.series.length} pontos</span>
                            <span className="text-[10px] uppercase tracking-wider text-slate-400 font-mono">Pico R$ {Math.max(0, ...consolidated.series.map((b) => b.total)).toFixed(0)}</span>
                        </div>
                        <RevenueChart series={consolidated.series} primary={brand.primaryColor} />
                    </div>

                    <div className="grid lg:grid-cols-2 gap-4">
                        <div data-testid="fin-cons-top-pros" className="rounded-xl border border-white/5 bg-black/40 p-4">
                            <header className="flex items-center justify-between pb-2 border-b border-white/5 mb-3">
                                <h4 className="text-sm font-bold text-amber-200"><i className="fa-solid fa-trophy mr-2"></i>Top colaboradores</h4>
                                <span className="text-[10px] uppercase tracking-wider text-slate-500">por receita</span>
                            </header>
                            {consolidated.topPros.length === 0 ? (
                                <div className="text-center text-slate-500 text-sm py-6">Sem dados no período.</div>
                            ) : (
                                <div className="space-y-2.5">
                                    {consolidated.topPros.slice(0, 6).map((p, idx) => {
                                        const max = consolidated.topPros[0].revenue || 1;
                                        const pct = (p.revenue / max) * 100;
                                        return (
                                            <div key={p.id} data-testid={`fin-cons-pro-${p.id}`} className="space-y-1">
                                                <div className="flex items-center gap-2 text-xs">
                                                    <img src={p.avatar} alt="" className="h-7 w-7 rounded-lg object-cover ring-1 ring-amber-500/25" />
                                                    <span className={`flex-1 truncate font-semibold ${idx === 0 ? "text-amber-100" : "text-slate-200"}`}>
                                                        {idx === 0 && <i className="fa-solid fa-crown text-amber-400 text-[10px] mr-1.5"></i>}
                                                        {p.name}
                                                    </span>
                                                    <span className="font-mono text-[11px] text-amber-300 shrink-0">R$ {p.revenue.toFixed(0)}</span>
                                                    <span className="font-mono text-[10px] text-slate-500 shrink-0 w-10 text-right">{p.count}×</span>
                                                </div>
                                                <div className="h-2 bg-slate-950/60 rounded-full overflow-hidden border border-amber-500/10 ml-9">
                                                    <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: idx === 0 ? "linear-gradient(90deg, #d4af37, #fbbf24)" : "linear-gradient(90deg, rgba(212,175,55,0.55), rgba(251,191,36,0.4))", boxShadow: idx === 0 ? "0 0 12px rgba(212,175,55,0.4)" : "none" }}></div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        <div data-testid="fin-cons-top-services" className="rounded-xl border border-white/5 bg-black/40 p-4">
                            <header className="flex items-center justify-between pb-2 border-b border-white/5 mb-3">
                                <h4 className="text-sm font-bold text-amber-200"><i className="fa-solid fa-fire mr-2"></i>Serviços mais vendidos</h4>
                                <span className="text-[10px] uppercase tracking-wider text-slate-500">por receita</span>
                            </header>
                            {consolidated.topServices.length === 0 ? (
                                <div className="text-center text-slate-500 text-sm py-6">Sem dados no período.</div>
                            ) : (
                                <div className="space-y-2.5">
                                    {consolidated.topServices.slice(0, 6).map((s, idx) => {
                                        const max = consolidated.topServices[0].revenue || 1;
                                        const pct = (s.revenue / max) * 100;
                                        return (
                                            <div key={s.name} className="space-y-1">
                                                <div className="flex items-center gap-2 text-xs">
                                                    <span className={`flex-1 truncate font-semibold ${idx === 0 ? "text-amber-100" : "text-slate-200"}`}>
                                                        {idx === 0 && <i className="fa-solid fa-fire text-orange-400 text-[10px] mr-1.5"></i>}
                                                        {s.name}
                                                    </span>
                                                    <span className="font-mono text-[11px] text-amber-300 shrink-0">R$ {s.revenue.toFixed(0)}</span>
                                                    <span className="font-mono text-[10px] text-slate-500 shrink-0 w-10 text-right">{s.count}×</span>
                                                </div>
                                                <div className="h-2 bg-slate-950/60 rounded-full overflow-hidden border border-amber-500/10">
                                                    <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: idx === 0 ? "linear-gradient(90deg, #d4af37, #fbbf24)" : "linear-gradient(90deg, rgba(212,175,55,0.55), rgba(251,191,36,0.4))" }}></div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Weekly chart · Serviços vs Produtos */}
                <div className="rounded-2xl border border-white/5 bg-slate-900/40 backdrop-blur-xl p-6">
                    <div className="flex items-center justify-between mb-5">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Performance Semanal</div>
                            <h3 className="salon-heading text-lg text-white mt-1">Serviços vs Produtos</h3>
                        </div>
                        <div className="flex items-center gap-4 text-xs">
                            <span className="flex items-center gap-2"><span className="h-2 w-2 rounded-full" style={{ background: brand.primaryColor }}></span><span className="text-slate-400">Serviços</span></span>
                            <span className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-slate-500"></span><span className="text-slate-400">Produtos</span></span>
                        </div>
                    </div>
                    <div className="flex items-end gap-3 h-56">
                        {WEEKLY_SALES.map((d, idx) => {
                            const total = d.services + d.products;
                            const totalH = (total / maxSales) * 100;
                            const serviceH = (d.services / total) * totalH;
                            const productH = totalH - serviceH;
                            return (
                                <div key={d.day} className="flex-1 flex flex-col items-center gap-2">
                                    <div data-testid={`bar-${d.day}`} className="w-full max-w-[44px] rounded-t-lg overflow-hidden flex flex-col justify-end relative" style={{ height: "200px" }}>
                                        <div className="w-full transition-all" style={{ height: `${productH}%`, background: "rgba(148, 163, 184, 0.5)", animation: `growUp 0.6s ease-out ${idx * 60}ms both` }}></div>
                                        <div className="w-full transition-all" style={{ height: `${serviceH}%`, background: `linear-gradient(180deg, ${brand.primaryColor}, ${brand.primaryColor}aa)`, animation: `growUp 0.6s ease-out ${idx * 60 + 100}ms both` }}></div>
                                    </div>
                                    <span className="text-[11px] uppercase tracking-wider text-slate-500">{d.day}</span>
                                    <span className="text-[10px] font-mono text-slate-400">{(total / 1000).toFixed(1)}k</span>
                                </div>
                            );
                        })}
                    </div>
                    <style>{`@keyframes growUp { from { height: 0%; } }`}</style>
                </div>
            </SubViewShell>
        );
    }

    // ═══ VIEW 2 — COMISSÕES & EQUIPE ══════════════════════════════════════════
    if (view === "comissoes") {
        const pending = professionals.filter((p) => (p.commissionAccrued ?? p.commission_accrued ?? 0) > 0);
        const hasPending = pending.length > 0;
        return (
            <SubViewShell
                view={view} title="Comissões & Equipe" onBack={() => setView("hub")} onLock={onLock}
                actions={hasPending && (
                    <button
                        data-testid="pay-all-commissions-btn"
                        onClick={() => {
                            if (window.confirm(`Quitar todas as ${pending.length} comissões pendentes?`)) {
                                pending.forEach((p) => payCommission && payCommission(p.id));
                                toast.success("Todas comissões quitadas");
                            }
                        }}
                        className="min-h-[44px] px-4 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs uppercase tracking-widest"
                    >
                        <i className="fa-solid fa-check-double mr-1.5"></i>Quitar todas
                    </button>
                )}
            >
                {/* KPI único destaque */}
                <div data-testid="commissions-total-pending" className="rounded-2xl border border-amber-500/30 bg-amber-500/10 backdrop-blur-xl p-6 text-center">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300/80 font-bold">Total de comissões pendentes</div>
                    <div className="salon-heading text-5xl text-amber-100 font-mono mt-2">R$ {metrics.pendingCommissions.toFixed(2)}</div>
                    <div className="text-xs text-amber-300/70 mt-1">{pending.length} profissional{pending.length !== 1 ? "is" : ""} com valores a receber</div>
                </div>

                {!hasPending ? (
                    <div className="rounded-2xl border border-emerald-500/25 bg-emerald-500/5 p-10 text-center">
                        <i className="fa-solid fa-circle-check text-4xl text-emerald-400 mb-3"></i>
                        <h4 className="salon-heading text-lg text-emerald-200">Todas as comissões estão em dia</h4>
                        <p className="text-xs text-slate-400 mt-1">Nenhum valor pendente no momento. 🎉</p>
                    </div>
                ) : (
                    <div className="rounded-2xl border border-white/5 bg-slate-900/40 backdrop-blur-xl p-6">
                        <h3 className="salon-heading text-lg text-white mb-4">Comissões Acumuladas</h3>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-[10px] uppercase tracking-[0.25em] text-slate-500">
                                        <th className="text-left py-2 px-2">Profissional</th>
                                        <th className="text-left py-2 px-2">Função</th>
                                        <th className="text-right py-2 px-2">Taxa</th>
                                        <th className="text-right py-2 px-2">Acumulado</th>
                                        <th className="text-right py-2 px-2">Ação</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {professionals.map((p) => {
                                        const accrued = p.commissionAccrued ?? p.commission_accrued ?? 0;
                                        const isPending = accrued > 0;
                                        return (
                                            <tr key={p.id} data-testid={`commission-row-${p.id}`} className="border-t border-white/5">
                                                <td className="py-3 px-2">
                                                    <div className="flex items-center gap-3">
                                                        <img src={p.avatar} alt="" className="h-9 w-9 rounded-full object-cover" />
                                                        <span className="font-semibold text-white">{p.name}</span>
                                                    </div>
                                                </td>
                                                <td className="py-3 px-2 text-slate-400">{p.role}</td>
                                                <td className="py-3 px-2 text-right font-mono text-slate-300">{((p.commissionRate ?? p.commission_rate ?? 0) * 100).toFixed(0)}%</td>
                                                <td className="py-3 px-2 text-right font-mono font-bold" style={{ color: brand.primaryColor }}>R$ {accrued.toFixed(2)}</td>
                                                <td className="py-3 px-2 text-right">
                                                    {isPending ? (
                                                        <button
                                                            data-testid={`pay-commission-${p.id}`}
                                                            onClick={() => { if (payCommission) { payCommission(p.id); toast.success(`Comissão de ${p.name} quitada`); } }}
                                                            className="min-h-[32px] px-3 rounded-lg bg-amber-500/20 hover:bg-amber-500 border border-amber-500/40 hover:border-amber-400 text-amber-200 hover:text-slate-950 text-[10px] uppercase tracking-wider font-bold transition-all"
                                                        >
                                                            <i className="fa-solid fa-check mr-1"></i>Quitar
                                                        </button>
                                                    ) : (
                                                        <span className="text-emerald-400 text-[10px] uppercase tracking-widest">Em dia</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </SubViewShell>
        );
    }

    // ═══ VIEW 3 — CONTAS A PAGAR ══════════════════════════════════════════════
    if (view === "contas") {
        return (
            <SubViewShell view={view} title="Contas a Pagar" onBack={() => setView("hub")} onLock={onLock}>
                <PayablesView
                    payables={payables}
                    markPaid={markPayablePaid}
                    upsertPayable={upsertPayable}
                    deletePayable={deletePayable}
                    metrics={metrics}
                />
            </SubViewShell>
        );
    }

    // ═══ VIEW 4 — PULSE DO SALÃO ══════════════════════════════════════════════
    if (view === "pulse") {
        return (
            <SubViewShell view={view} title="Pulse do Salão" onBack={() => setView("hub")} onLock={onLock}>
                <PulseView brand={brand} professionals={professionals} metrics={metrics} comandas={comandas} />
            </SubViewShell>
        );
    }

    return null;
};

// ─── Shell compartilhado das sub-views (BackButton + header + Bloquear) ────
const SubViewShell = ({ view, title, onBack, onLock, actions, children }) => (
    <div data-testid={`financial-view-${view}`} className="space-y-5">
        <BackButton onBack={onBack} />
        <header className="flex items-center justify-between gap-4 flex-wrap">
            <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500">
                    Módulo E · Acesso restrito
                </div>
                <h2 className="salon-heading text-2xl text-white mt-1">{title}</h2>
            </div>
            <div className="flex items-center gap-2">
                {actions}
                <button
                    data-testid="lock-admin-btn"
                    onClick={onLock}
                    className="min-h-[44px] px-4 rounded-xl border border-white/10 hover:border-white/30 text-xs uppercase tracking-wider text-slate-300 hover:text-white transition-all"
                >
                    <i className="fa-solid fa-lock mr-2"></i>Bloquear
                </button>
            </div>
        </header>
        {children}
    </div>
);

// ─── PayablesView (view 3 — Contas a Pagar) ────────────────────────────────
const PayablesView = ({ payables, markPaid, upsertPayable, deletePayable, metrics }) => {
    const [filter, setFilter] = useState("todas"); // todas | vencidas | avencer | pagas
    const [editing, setEditing] = useState(null);

    const now = Date.now();
    const weekEnd = now + 7 * 86400000;
    const startOfDay = new Date(); startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = startOfDay.getTime() + 86400000;

    const overdueToday = payables.filter((p) => p.status === "open" && p.dueDate >= startOfDay.getTime() && p.dueDate < endOfDay);
    const dueThisWeek = payables.filter((p) => p.status === "open" && p.dueDate >= now && p.dueDate < weekEnd);

    const filtered = payables.filter((p) => {
        if (filter === "vencidas") return p.status === "open" && p.dueDate < now;
        if (filter === "avencer") return p.status === "open" && p.dueDate >= now;
        if (filter === "pagas") return p.status === "paid";
        return true;
    }).sort((a, b) => a.dueDate - b.dueDate);

    const fmtDate = (ms) => new Date(ms).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
    const isOverdue = (p) => p.status === "open" && p.dueDate < now;

    return (
        <div className="space-y-5" data-testid="payables-view">
            {/* KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="rounded-2xl border border-white/5 bg-slate-900/40 p-4">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Total em aberto</div>
                    <div className="salon-heading text-2xl text-white font-mono mt-1">R$ {metrics.totalOpenPayables.toFixed(2)}</div>
                </div>
                <div className="rounded-2xl border border-red-500/25 bg-red-500/5 p-4">
                    <div className="text-[10px] uppercase tracking-widest text-red-300/80">Vencidas hoje</div>
                    <div className="salon-heading text-2xl text-red-200 font-mono mt-1">{overdueToday.length}</div>
                </div>
                <div className="rounded-2xl border border-amber-500/25 bg-amber-500/5 p-4">
                    <div className="text-[10px] uppercase tracking-widest text-amber-300/80">Vencendo esta semana</div>
                    <div className="salon-heading text-2xl text-amber-200 font-mono mt-1">{dueThisWeek.length}</div>
                </div>
            </div>

            {/* Header + botão nova + filtros */}
            <div className="rounded-2xl border border-white/5 bg-slate-900/40 backdrop-blur-xl p-5 space-y-4">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-1 p-1 rounded-xl border border-white/8 bg-black/40">
                        {[{id:"todas",label:"Todas"},{id:"vencidas",label:"Vencidas"},{id:"avencer",label:"A vencer"},{id:"pagas",label:"Pagas"}].map((f) => (
                            <button
                                key={f.id}
                                data-testid={`payables-filter-${f.id}`}
                                onClick={() => setFilter(f.id)}
                                className={`min-h-[36px] px-3 rounded-lg text-[10px] uppercase tracking-wider font-bold transition-all ${filter === f.id ? "bg-amber-500 text-slate-950" : "text-slate-400 hover:text-amber-200"}`}
                            >{f.label}</button>
                        ))}
                    </div>
                    <button
                        data-testid="payables-new-btn"
                        onClick={() => setEditing({ id: null, name: "", amount: "", dueDate: new Date().toISOString().slice(0, 10), category: "Geral" })}
                        className="min-h-[40px] px-4 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs uppercase tracking-widest inline-flex items-center gap-1.5"
                    >
                        <i className="fa-solid fa-plus"></i>Nova conta
                    </button>
                </div>

                {filtered.length === 0 ? (
                    <div className="text-center text-slate-500 text-sm py-10 border border-white/5 rounded-xl">
                        Nenhuma conta {filter === "todas" ? "cadastrada" : `no filtro ${filter}`}.
                    </div>
                ) : (
                    <div className="space-y-2">
                        {filtered.map((p) => (
                            <div
                                key={p.id}
                                data-testid={`payable-row-${p.id}`}
                                className={`rounded-xl border p-3 flex items-center gap-3 ${
                                    p.status === "paid"
                                        ? "border-white/5 bg-black/30 opacity-60"
                                        : isOverdue(p)
                                            ? "border-red-500/25 bg-red-500/5"
                                            : "border-white/8 bg-black/40"
                                }`}
                            >
                                <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${
                                    p.status === "paid" ? "bg-emerald-500/10 text-emerald-300" :
                                    isOverdue(p) ? "bg-red-500/15 text-red-300" : "bg-amber-500/10 text-amber-300"
                                }`}>
                                    <i className={`fa-solid ${p.status === "paid" ? "fa-check" : isOverdue(p) ? "fa-triangle-exclamation" : "fa-file-invoice-dollar"}`}></i>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm text-white font-semibold truncate">{p.name}</div>
                                    <div className="text-[11px] text-slate-500">
                                        {p.category || "Geral"} · {p.status === "paid" ? `Pago em ${fmtDate(p.paidAt || p.dueDate)}` : `Vence ${fmtDate(p.dueDate)}`}
                                    </div>
                                </div>
                                <div className="text-right shrink-0">
                                    <div className="salon-heading text-base text-white font-mono">R$ {Number(p.amount).toFixed(2)}</div>
                                </div>
                                <div className="flex gap-1 shrink-0">
                                    {p.status !== "paid" && (
                                        <button
                                            data-testid={`payable-pay-${p.id}`}
                                            onClick={() => { markPaid && markPaid(p.id); toast.success("Conta paga"); }}
                                            className="h-8 px-2 rounded-lg border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 text-[10px] uppercase tracking-wider font-bold"
                                        ><i className="fa-solid fa-check mr-1"></i>Pagar</button>
                                    )}
                                    <button
                                        data-testid={`payable-edit-${p.id}`}
                                        onClick={() => setEditing({ ...p, dueDate: new Date(p.dueDate).toISOString().slice(0, 10) })}
                                        className="h-8 w-8 rounded-lg border border-white/10 text-slate-300 hover:text-amber-300 hover:border-amber-500/40 text-xs"
                                    ><i className="fa-solid fa-pen"></i></button>
                                    <button
                                        data-testid={`payable-delete-${p.id}`}
                                        onClick={() => { if (window.confirm(`Excluir "${p.name}"?`)) { deletePayable && deletePayable(p.id); toast.success("Conta excluída"); } }}
                                        className="h-8 w-8 rounded-lg border border-red-500/30 text-red-300 hover:bg-red-500/10 text-xs"
                                    ><i className="fa-solid fa-trash"></i></button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {editing && (
                <PayableEditor
                    payable={editing}
                    onClose={() => setEditing(null)}
                    onSave={(data) => {
                        upsertPayable && upsertPayable(data);
                        toast.success(editing.id ? "Conta atualizada" : "Conta criada");
                        setEditing(null);
                    }}
                />
            )}
        </div>
    );
};

const PayableEditor = ({ payable, onClose, onSave }) => {
    const [form, setForm] = useState({
        name: payable.name || "",
        amount: payable.amount || "",
        dueDate: payable.dueDate || new Date().toISOString().slice(0, 10),
        category: payable.category || "Geral",
    });
    const save = () => {
        if (!form.name.trim() || !form.amount) { toast.error("Nome e valor são obrigatórios"); return; }
        onSave({
            ...(payable.id ? { id: payable.id } : {}),
            name: form.name.trim(),
            amount: Number(form.amount),
            dueDate: new Date(form.dueDate).getTime(),
            category: form.category,
            status: payable.status || "open",
        });
    };
    return (
        <div className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="payable-editor-modal">
            <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-2xl border border-amber-500/30 bg-slate-950 p-6 space-y-3">
                <h3 className="salon-heading text-lg text-white">{payable.id ? "Editar conta" : "Nova conta"}</h3>
                <input data-testid="payable-input-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nome (ex: Aluguel)" className="w-full h-10 px-3 rounded-lg border border-white/10 bg-slate-900/70 text-sm text-white focus:border-amber-500/50 focus:outline-none" />
                <div className="grid grid-cols-2 gap-2">
                    <input data-testid="payable-input-amount" type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="Valor R$" className="h-10 px-3 rounded-lg border border-white/10 bg-slate-900/70 text-sm text-white font-mono focus:border-amber-500/50 focus:outline-none" />
                    <input data-testid="payable-input-date" type="date" value={form.dueDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} className="h-10 px-3 rounded-lg border border-white/10 bg-slate-900/70 text-sm text-white focus:border-amber-500/50 focus:outline-none" />
                </div>
                <input data-testid="payable-input-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Categoria" className="w-full h-10 px-3 rounded-lg border border-white/10 bg-slate-900/70 text-sm text-white focus:border-amber-500/50 focus:outline-none" />
                <div className="flex gap-2 justify-end pt-2">
                    <button onClick={onClose} className="h-10 px-4 rounded-lg border border-white/10 text-slate-300 text-xs uppercase tracking-widest hover:bg-white/5">Cancelar</button>
                    <button data-testid="payable-save-btn" onClick={save} className="h-10 px-4 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs uppercase tracking-widest">Salvar</button>
                </div>
            </div>
        </div>
    );
};

// ─── PulseView (view 4 — status operacional em tempo real) ─────────────────
const PulseView = ({ brand, professionals, metrics, comandas }) => {
    const now = new Date();
    const nowMs = now.getTime();
    const in2h = nowMs + 2 * 3600000;
    const proInService = comandas.length; // proxy: comandas abertas ≈ pessoas em atendimento
    const proAvailable = Math.max(0, professionals.length - proInService);
    const waitlist = 3;
    const criticalStock = 2;
    const rating = 4.9;

    const cards = [
        { icon: "fa-chair", label: "Cadeiras ocupadas", value: `${proInService} de ${professionals.length}`, pct: (proInService / Math.max(1, professionals.length)) * 100 },
        { icon: "fa-hourglass-half", label: "Lista de espera", value: `${waitlist} clientes`, pct: waitlist * 20 },
        { icon: "fa-triangle-exclamation", label: "Estoque crítico", value: `${criticalStock} produtos`, pct: criticalStock * 25, tone: "red" },
        { icon: "fa-star", label: "Avaliação média", value: `${rating.toFixed(1)} ★`, pct: rating * 20, tone: "amber" },
    ];

    // Próximos agendamentos nas próximas 2h (mock a partir dos profissionais.transactions futuras se houver)
    const upcomingAppts = [];
    professionals.forEach((p) => {
        (p.transactions || []).forEach((t) => {
            if (t.date >= nowMs && t.date <= in2h) {
                upcomingAppts.push({ time: new Date(t.date).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }), pro: p.name, avatar: p.avatar, service: t.service });
            }
        });
    });

    return (
        <div className="space-y-5" data-testid="pulse-view">
            {/* Receita acumulada em tempo real */}
            <div className="rounded-2xl border border-emerald-500/25 bg-emerald-500/5 p-6 flex items-center justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-300/80 font-bold">Receita acumulada · tempo real</div>
                    <div className="salon-heading text-4xl text-emerald-100 font-mono mt-2">R$ {metrics.grossToday.toFixed(2)}</div>
                    <div className="text-xs text-slate-400 mt-1">{metrics.completed} atendimentos hoje · ticket médio R$ {metrics.avgTicket.toFixed(2)}</div>
                </div>
                <div className="h-16 w-16 rounded-full bg-emerald-500/15 border border-emerald-400/40 text-emerald-300 flex items-center justify-center text-2xl animate-pulse">
                    <i className="fa-solid fa-heart-pulse"></i>
                </div>
            </div>

            {/* Cards de status */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {cards.map((s) => (
                    <div key={s.label} data-testid={`pulse-card-${s.label.replace(/\s+/g, '-')}`} className="rounded-2xl border border-white/5 bg-slate-900/40 p-4">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs text-slate-300 flex items-center gap-2">
                                <i className={`fa-solid ${s.icon} ${s.tone === "red" ? "text-red-400" : s.tone === "amber" ? "text-amber-400" : "text-slate-400"}`}></i>
                                {s.label}
                            </span>
                            <span className="text-sm font-mono text-white font-semibold">{s.value}</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-slate-950/60 overflow-hidden">
                            <div className="h-full rounded-full transition-all duration-700" style={{ width: `${Math.min(100, s.pct)}%`, background: s.tone === "red" ? "#f87171" : brand.primaryColor }}></div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Colaboradores em atendimento vs disponíveis */}
            <div className="rounded-2xl border border-white/5 bg-slate-900/40 p-5" data-testid="pulse-collaborators">
                <div className="flex items-center justify-between mb-3">
                    <h4 className="salon-heading text-base text-white">Colaboradores agora</h4>
                    <span className="text-[10px] uppercase tracking-widest text-slate-500">
                        <span className="text-emerald-300">{proInService} atendendo</span> · <span className="text-slate-400">{proAvailable} disponíveis</span>
                    </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {professionals.map((p, idx) => {
                        const busy = idx < proInService;
                        return (
                            <div key={p.id} className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 ${
                                busy ? "border-emerald-500/25 bg-emerald-500/5" : "border-white/5 bg-black/30"
                            }`}>
                                <img src={p.avatar} alt="" className="h-7 w-7 rounded-full object-cover ring-1 ring-white/10" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-xs text-white truncate">{p.name}</div>
                                    <div className={`text-[9px] uppercase tracking-widest ${busy ? "text-emerald-300" : "text-slate-500"}`}>
                                        {busy ? "Em atendimento" : "Disponível"}
                                    </div>
                                </div>
                                <span className={`h-2 w-2 rounded-full ${busy ? "bg-emerald-400 animate-pulse" : "bg-slate-500"}`}></span>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Próximos agendamentos 2h */}
            <div className="rounded-2xl border border-white/5 bg-slate-900/40 p-5" data-testid="pulse-upcoming">
                <div className="flex items-center justify-between mb-3">
                    <h4 className="salon-heading text-base text-white">Próximas 2 horas</h4>
                    <span className="text-[10px] uppercase tracking-widest text-slate-500">{upcomingAppts.length} agendamento{upcomingAppts.length !== 1 ? "s" : ""}</span>
                </div>
                {upcomingAppts.length === 0 ? (
                    <div className="text-center text-slate-500 text-sm py-6">Nenhum agendamento nas próximas 2 horas.</div>
                ) : (
                    <div className="space-y-1.5">
                        {upcomingAppts.slice(0, 6).map((a, i) => (
                            <div key={i} className="flex items-center gap-3 rounded-lg border border-white/5 bg-black/30 px-3 py-2">
                                <span className="text-sm font-mono text-amber-300 shrink-0">{a.time}</span>
                                <img src={a.avatar} alt="" className="h-7 w-7 rounded-full object-cover ring-1 ring-white/10" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-xs text-white truncate">{a.pro}</div>
                                    <div className="text-[10px] text-slate-500 truncate">{a.service}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

const Kpi = ({ label, value, icon, color = "primary" }) => {
    const { brand } = useSalon();
    const accent = color === "emerald" ? "#10b981" : color === "amber" ? "#f59e0b" : brand.primaryColor;
    return (
        <div className="rounded-2xl border border-white/5 bg-slate-900/40 backdrop-blur-xl p-5">
            <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">{label}</div>
                <div
                    className="h-9 w-9 rounded-lg flex items-center justify-center"
                    style={{ background: `${accent}22`, color: accent }}
                >
                    <i className={`fa-solid ${icon}`}></i>
                </div>
            </div>
            <div className="salon-heading text-2xl text-white mt-3" style={{ color: accent }}>
                {value}
            </div>
        </div>
    );
};

const ConsolidatedKpi = ({ icon, label, value, sub, accent = "amber", testId }) => {
    const accents = {
        emerald: { iconBg: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40", val: "text-emerald-100" },
        amber: { iconBg: "bg-amber-500/15 text-amber-300 border-amber-500/40", val: "text-amber-100" },
        slate: { iconBg: "bg-slate-700/40 text-slate-300 border-white/10", val: "text-slate-100" },
    };
    const a = accents[accent] || accents.amber;
    return (
        <div data-testid={testId} className="rounded-xl border border-white/8 bg-black/45 p-3.5 flex items-center gap-3">
            <div className={`h-10 w-10 rounded-lg border ${a.iconBg} flex items-center justify-center flex-shrink-0`}>
                <i className={`fa-solid ${icon}`}></i>
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
                <div className={`salon-heading text-xl ${a.val} font-mono leading-tight mt-0.5 truncate`}>{value}</div>
                {sub && <div className="text-[10px] text-slate-500 mt-0.5 truncate">{sub}</div>}
            </div>
        </div>
    );
};

const RevenueChart = ({ series, primary }) => {
    if (!series || series.length === 0) {
        return <div className="text-center text-slate-500 text-sm py-8">Sem dados no período.</div>;
    }
    const max = Math.max(1, ...series.map((b) => b.total));
    return (
        <div className="flex items-end gap-1 h-32" data-testid="revenue-chart">
            {series.map((b, i) => {
                const pct = (b.total / max) * 100;
                return (
                    <div key={i} className="flex-1 flex flex-col items-center justify-end gap-1 group" title={`${b.label}: R$ ${b.total.toFixed(2)} (${b.count} atendimentos)`}>
                        <div className="text-[8px] font-mono text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity">
                            {b.total > 0 ? (b.total / 1000).toFixed(1) + "k" : ""}
                        </div>
                        <div
                            className="w-full rounded-t-sm transition-all"
                            style={{
                                height: `${Math.max(2, pct)}%`,
                                background: b.total > 0 ? `linear-gradient(180deg, ${primary || "#d4af37"}, ${primary || "#d4af37"}88)` : "rgba(148,163,184,0.15)",
                                minHeight: 2,
                            }}
                        ></div>
                    </div>
                );
            })}
        </div>
    );
};

export default FinancialPanel;
